<script lang="ts">
  import type { PortableTextBlock } from "@portabletext/types";
  import type { Cover, ImageWithMeta, Teaser } from "$lib/types/shared";
  import Page from "$lib/Page.svelte";
  import Lead from "$lib/Lead.svelte";
  import CustomerService from "$lib/CustomerServiceBanner.svelte";
  import { contentBlockComponents } from "$lib/portableText/utils";
  import { PortableText } from "@portabletext/svelte";
  import Picture from "$lib/Picture.svelte";
  import { breakpoints as br } from "$lib/utils/getBreakpoints";
  import AppTeaser from "$lib/AppTeaser.svelte";
  import Faq from "$lib/FAQ.svelte";
  import Graph from "./Graph.svelte";
  import HandMoney from "./HandMoney.svelte";
  import Battery from "./Battery.svelte";

  interface Props {
    data: {
      cover: Readonly<Cover>;
      lead: string;
      introduction: PortableTextBlock[];
      advantages_title: string;
      advantages_cols: {
        content: PortableTextBlock[];
      }[];
      how_it_works: PortableTextBlock[];
      how_it_works_image: ImageWithMeta;
      get_started: PortableTextBlock[];
      app_teaser: Teaser;
      faq: {
        heading: string;
        items: {
          _id: string;
          question: string;
          answer: PortableTextBlock[];
        }[];
      };
    };
  }

  let { data }: Props = $props();

  const page = {
    title: data?.cover?.title || "Smartlading",
    description: data?.lead || "Vi tilbyr smartlading til våre privatkunder.",
  };
</script>

<svelte:head>
  <meta name="robots" content="noindex" />
</svelte:head>

<div class="page">
  <Page data={page} descriptionAsLead={false} contentNestingLevel={2} jsonLd>
    <div class="children-my-2">
      {#if data?.lead}
        <Lead class="laptop:text-h2"><strong>{data?.lead}</strong></Lead>
      {/if}

      {#if data?.introduction}
        <div class="text-lead">
          <PortableText value={data?.introduction} components={contentBlockComponents} />
        </div>
      {/if}
    </div>

    {#if data?.advantages_title && data?.advantages_cols?.length === 3}
      <section class="text-center">
        <h2 class="my-10">{data.advantages_title}</h2>

        <ul class="list-none p-0 grid tablet:grid-cols-3 gap-x-8 gap-y-4">
          {#each data?.advantages_cols as col, i (col)}
            {@const SvelteComponent = i === 0 ? Graph : i === 1 ? HandMoney : Battery}
            <li>
              <SvelteComponent />

              {#if col}
                <PortableText value={col.content} components={contentBlockComponents} />
              {/if}
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    {#if data?.how_it_works}
      <section class="box mx-edge-x">
        <div class="grid tablet:grid-cols-[1fr_auto] items-center gap-4 tablet:p-4">
          <div class="laptop:text-lead">
            <PortableText value={data?.how_it_works} components={contentBlockComponents} />
          </div>
          <!-- how_it_works_image -->
          <div class="text-center">
            <Picture
              src={data?.how_it_works_image?.src || ""}
              width={data?.how_it_works_image?.asset?.metadata?.dimensions?.width}
              height={data?.how_it_works_image?.asset?.metadata?.dimensions?.height}
              srcset={data?.how_it_works_image?.srcset}
              srcsetWebp={data?.how_it_works_image?.srcsetWebp}
              sizes={`(min-width: ${br.wide}) 30vw, (min-width: ${br.desktop}) 40vw, (min-width: ${br.tablet}) 45vw, 90vw`}
              alt={data?.how_it_works_image?.alt || ""}
              class="max-w-[25em]"
            />
          </div>
        </div>
      </section>
    {/if}

    <section>
      {#if data?.get_started}
        <div class="laptop:text-lead">
          <PortableText value={data?.get_started} components={contentBlockComponents} />
        </div>
      {/if}
    </section>

    <AppTeaser
      title="Ikke lastet ned appen enda?"
      class="my-10"
      image={data?.app_teaser?.image}
      description={data?.app_teaser?.description}
    />

    {#if data?.faq?.items}
      <!-- FAQ -->
      <section class="my-12">
        <h2>{data.faq.heading || `Spørsmål og svar`}</h2>
        <div class="border-solid border-3 rounded-2xl p-2">
          {#each data.faq.items as answer (answer)}
            <Faq {answer} />
          {/each}
        </div>
      </section>
    {/if}

    <CustomerService class="children-mx-0" />
  </Page>
</div>

<style lang="postcss">
  :where(:global(.page)) :where(:global(section)) {
    margin-block: var(--s-14);
  }

  .box {
    --_box-bg: var(--primary-light);
    background-color: var(--_box-bg);
    border-radius: 1rem;
    padding: var(--s-4) var(--s-3);
  }

  .box > :global(*) {
    margin-inline: 0;
  }

  @media (--tablet) {
    .box {
      padding-inline: var(--s-5);
    }
  }

  @media (--desktop) {
    .page :global(h2) {
      font-size: var(--text-h1);
    }
  }
</style>
